// ═══════════════════════════════════════════════════════════════
// MÓDULO: Personas Relacionadas
// Dependientes, familiares, contactos de emergencia, beneficiarios
// Fuente de datos para SS (cápita), ISR (dep. adicional) y más
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Users, Shield, AlertCircle, Heart, User, Phone, MapPin,
  Calendar, Plus, Search, Edit2, Trash2,
  ChevronDown, X, Save, RefreshCw, CheckCircle, XCircle
} from 'lucide-react';
import useEmployees from '../../hooks/useEmployees';
import {
  getRelatedPersons,
  createRelatedPerson,
  updateRelatedPerson,
  deleteRelatedPerson,
} from '../../api/relatedPersons';
import Pagination from '../common/Pagination';

const PAGE_SIZE = 15;

// ── Catálogos ─────────────────────────────────────────────────

const RELATIONSHIPS = [
  { value: 'conyuge',  label: 'Cónyuge'   },
  { value: 'hijo',     label: 'Hijo'      },
  { value: 'hija',     label: 'Hija'      },
  { value: 'padre',    label: 'Padre'     },
  { value: 'madre',    label: 'Madre'     },
  { value: 'hermano',  label: 'Hermano'   },
  { value: 'hermana',  label: 'Hermana'   },
  { value: 'abuelo',   label: 'Abuelo'    },
  { value: 'abuela',   label: 'Abuela'    },
  { value: 'nieto',    label: 'Nieto'     },
  { value: 'nieta',    label: 'Nieta'     },
  { value: 'otro',     label: 'Otro'      },
];

const REL_MAP = Object.fromEntries(RELATIONSHIPS.map(r => [r.value, r.label]));

const ROLES = [
  {
    key:      'is_health_dependent',
    label:    'Dep. Salud',
    full:     'Dependiente de Salud',
    desc:     'Genera cápita en Seguridad Social (SFS/ARS)',
    bg:       'bg-blue-100',   text: 'text-blue-700',   border: 'border-blue-300',
    activeBg: 'bg-blue-600',   Icon: Shield,
  },
  {
    key:      'is_additional_dependent',
    label:    'Dep. Adicional',
    full:     'Dependiente Adicional',
    desc:     'Aplica en módulo de Beneficios Fiscales / ISR (DGII)',
    bg:       'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300',
    activeBg: 'bg-purple-600', Icon: Users,
  },
  {
    key:      'is_emergency_contact',
    label:    'Emergencia',
    full:     'Contacto de Emergencia',
    desc:     'Persona a contactar en caso de emergencia',
    bg:       'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300',
    activeBg: 'bg-orange-500', Icon: AlertCircle,
  },
  {
    key:      'is_beneficiary',
    label:    'Beneficiario',
    full:     'Beneficiario',
    desc:     'Beneficiario de prestaciones laborales o seguros',
    bg:       'bg-green-100',  text: 'text-green-700',  border: 'border-green-300',
    activeBg: 'bg-green-600',  Icon: Heart,
  },
];

const GENDERS = [
  { value: '',          label: 'Sin especificar' },
  { value: 'masculino', label: 'Masculino'       },
  { value: 'femenino',  label: 'Femenino'        },
  { value: 'otro',      label: 'Otro'            },
];

const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const calcAge = (birthDate) => {
  if (!birthDate) return null;
  const today = new Date();
  const dob   = new Date(birthDate);
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
};

// ── Stat card ─────────────────────────────────────────────────
function StatCard({ Icon: IconComp, iconBg, iconCls, label, value, sub }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
          <IconComp className={`w-5 h-5 ${iconCls}`} />
        </div>
      </div>
    </div>
  );
}

// ── Modal de formulario ────────────────────────────────────────
function PersonModal({ person, employees, onSave, onClose }) {
  const editing = !!person;

  const [form, setForm] = useState({
    employee_id:             person?.employee_id             || '',
    first_name:              person?.first_name              || '',
    last_name:               person?.last_name               || '',
    relationship:            person?.relationship            || 'hijo',
    birth_date:              person?.birth_date?.slice(0, 10) || '',
    gender:                  person?.gender                  || '',
    document_number:         person?.document_number         || '',
    phone:                   person?.phone                   || '',
    address:                 person?.address                 || '',
    is_health_dependent:     person?.is_health_dependent     ?? false,
    is_additional_dependent: person?.is_additional_dependent ?? false,
    is_emergency_contact:    person?.is_emergency_contact    ?? false,
    is_beneficiary:          person?.is_beneficiary          ?? false,
    quincena_descuento:      person?.quincena_descuento      || 'AMBAS',
    notes:                   person?.notes                   || '',
    active:                  person?.active                  ?? true,
  });

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const toggleRole = (key) => {
    setForm(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.employee_id) return setError('Selecciona un empleado');
    if (!form.first_name.trim()) return setError('El nombre es requerido');
    if (!form.last_name.trim()) return setError('El apellido es requerido');
    if (!form.relationship) return setError('La relación es requerida');

    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        first_name:    form.first_name.trim(),
        last_name:     form.last_name.trim(),
        birth_date:    form.birth_date || null,
        gender:        form.gender     || null,
      };
      if (editing) {
        await updateRelatedPerson(person.id, payload);
      } else {
        await createRelatedPerson(payload);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl border border-gray-200 w-full max-w-2xl shadow-xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-sm font-semibold text-gray-900">
            {editing ? 'Editar Persona Relacionada' : 'Nueva Persona Relacionada'}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto">
          <div className="p-6 space-y-5">

            {/* Empleado */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                <User size={12} className="inline mr-1" />
                Empleado *
              </label>
              <div className="relative">
                <select
                  value={form.employee_id}
                  onChange={e => set('employee_id', e.target.value)}
                  disabled={editing}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white appearance-none disabled:bg-gray-50 disabled:text-gray-400"
                >
                  <option value="">Selecciona un empleado...</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.nombre} {emp.apellido} — {emp.employee_id}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Datos personales */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Datos Personales
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nombre *</label>
                  <input
                    type="text"
                    value={form.first_name}
                    onChange={e => set('first_name', e.target.value)}
                    placeholder="Nombre"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Apellido *</label>
                  <input
                    type="text"
                    value={form.last_name}
                    onChange={e => set('last_name', e.target.value)}
                    placeholder="Apellido"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Relación *</label>
                  <div className="relative">
                    <select
                      value={form.relationship}
                      onChange={e => set('relationship', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white appearance-none"
                    >
                      {RELATIONSHIPS.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Género</label>
                  <div className="relative">
                    <select
                      value={form.gender}
                      onChange={e => set('gender', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white appearance-none"
                    >
                      {GENDERS.map(g => (
                        <option key={g.value} value={g.value}>{g.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    <Calendar size={11} className="inline mr-1" />
                    Fecha de Nacimiento
                  </label>
                  <input
                    type="date"
                    value={form.birth_date}
                    onChange={e => set('birth_date', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    No. Documento
                  </label>
                  <input
                    type="text"
                    value={form.document_number}
                    onChange={e => set('document_number', e.target.value)}
                    placeholder="Cédula, pasaporte..."
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    <Phone size={11} className="inline mr-1" />
                    Teléfono
                  </label>
                  <input
                    type="text"
                    value={form.phone}
                    onChange={e => set('phone', e.target.value)}
                    placeholder="809-000-0000"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    <MapPin size={11} className="inline mr-1" />
                    Dirección
                  </label>
                  <input
                    type="text"
                    value={form.address}
                    onChange={e => set('address', e.target.value)}
                    placeholder="Dirección..."
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                  />
                </div>
              </div>
            </div>

            {/* Roles en el sistema */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Roles en el Sistema
              </p>
              <div className="grid grid-cols-2 gap-2">
                {ROLES.map(role => (
                  <button
                    key={role.key}
                    type="button"
                    onClick={() => toggleRole(role.key)}
                    className={`flex items-start gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                      form[role.key]
                        ? `${role.bg} ${role.border} ${role.text}`
                        : 'border-gray-200 text-gray-500 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      form[role.key] ? role.activeBg + ' text-white' : 'bg-gray-100 text-gray-400'
                    }`}>
                      <role.Icon size={14} />
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold ${form[role.key] ? role.text : 'text-gray-700'}`}>
                        {role.full}
                      </p>
                      <p className="text-xs text-gray-400 leading-tight mt-0.5">{role.desc}</p>
                    </div>
                    <div className={`ml-auto w-4 h-4 rounded-full border-2 flex-shrink-0 mt-1 ${
                      form[role.key] ? `${role.activeBg} border-transparent` : 'border-gray-300'
                    }`} />
                  </button>
                ))}
              </div>
            </div>

            {/* Quincena de descuento de cápita — solo visible para dependientes adicionales */}
            {form.is_additional_dependent && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-xs font-semibold text-purple-700 uppercase tracking-wider mb-2">
                  Cápita ARS — Quincena de Descuento
                </p>
                <p className="text-xs text-purple-600 mb-3">
                  Determina en qué quincena se aplica el descuento de cápita ARS para este dependiente.
                </p>
                <div className="relative">
                  <select
                    name="quincena_descuento"
                    value={form.quincena_descuento}
                    onChange={e => set('quincena_descuento', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-purple-300 rounded-lg focus:outline-none focus:border-purple-500 bg-white appearance-none"
                  >
                    <option value="AMBAS">Ambas quincenas (50% cada una)</option>
                    <option value="Q1">Solo Q1 (monto completo en Q1)</option>
                    <option value="Q2">Solo Q2 (monto completo en Q2)</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-purple-400 pointer-events-none" />
                </div>
              </div>
            )}

            {/* Notas + Estado */}
            <div className="grid grid-cols-2 gap-4 items-start">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notas</label>
                <textarea
                  value={form.notes}
                  onChange={e => set('notes', e.target.value)}
                  rows={2}
                  placeholder="Observaciones adicionales..."
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Estado</label>
                <button
                  type="button"
                  onClick={() => set('active', !form.active)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    form.active
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {form.active
                    ? <><CheckCircle size={14} /> Activo</>
                    : <><XCircle size={14} /> Inactivo</>}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
                <p className="text-xs text-red-700">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-40"
            >
              <Save size={14} />
              {saving ? 'Guardando...' : editing ? 'Actualizar' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════
export default function RelatedPersonsModule() {
  const { employees } = useEmployees();

  const [persons,    setPersons]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error,      setError]      = useState(null);
  const [showModal,  setShowModal]  = useState(false);
  const [editing,    setEditing]    = useState(null);

  // Filtros
  const [page,               setPage]               = useState(1);
  const [filterEmployee,     setFilterEmployee]     = useState('');
  const [filterRelationship, setFilterRelationship] = useState('');
  const [filterRole,         setFilterRole]         = useState('');
  const [filterActive,       setFilterActive]       = useState('true');
  const [searchText,         setSearchText]         = useState('');

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else         setRefreshing(true);
    setError(null);
    try {
      const filters = {};
      if (filterActive !== '') filters.active = filterActive === 'true';
      const data = await getRelatedPersons(filters);
      setPersons(data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar personas relacionadas');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filterActive]);

  useEffect(() => { load(); }, [load]);

  // Filtrado local
  const filtered = useMemo(() => {
    let list = persons;
    if (filterEmployee) list = list.filter(p => p.employee_id === filterEmployee);
    if (filterRelationship) list = list.filter(p => p.relationship === filterRelationship);
    if (filterRole === 'health')    list = list.filter(p => p.is_health_dependent);
    if (filterRole === 'additional') list = list.filter(p => p.is_additional_dependent);
    if (filterRole === 'emergency') list = list.filter(p => p.is_emergency_contact);
    if (filterRole === 'beneficiary') list = list.filter(p => p.is_beneficiary);
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      list = list.filter(p =>
        (`${p.first_name} ${p.last_name}`).toLowerCase().includes(q) ||
        (`${p.empleado_nombre} ${p.empleado_apellido}`).toLowerCase().includes(q) ||
        (p.empleado_codigo || '').toLowerCase().includes(q) ||
        (p.document_number || '').toLowerCase().includes(q) ||
        (p.phone || '').includes(q)
      );
    }
    setPage(1);
    return list;
  }, [persons, filterEmployee, filterRelationship, filterRole, searchText]);

  const totalPages  = Math.ceil(filtered.length / PAGE_SIZE);
  const pagedPersons = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Totales para stat cards
  const stats = useMemo(() => {
    const active = persons.filter(p => p.active);
    return {
      total:      active.length,
      health:     active.filter(p => p.is_health_dependent).length,
      emergency:  active.filter(p => p.is_emergency_contact).length,
      additional: active.filter(p => p.is_additional_dependent).length,
    };
  }, [persons]);

  const handleNew    = () => { setEditing(null); setShowModal(true); };
  const handleEdit   = (p) => { setEditing(p);   setShowModal(true); };
  const handleDelete = async (p) => {
    if (!window.confirm(`¿Eliminar a ${p.first_name} ${p.last_name} (${REL_MAP[p.relationship] || p.relationship})?`)) return;
    try {
      await deleteRelatedPerson(p.id);
      await load(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };
  const handleSaved = async () => {
    setShowModal(false);
    await load(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Cargando personas relacionadas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* ── Stat cards ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          Icon={Users}
          iconBg="bg-blue-100" iconCls="text-blue-600"
          label="Total Personas"
          value={stats.total}
          sub="Registros activos"
        />
        <StatCard
          Icon={Shield}
          iconBg="bg-sky-100" iconCls="text-sky-600"
          label="Dep. de Salud"
          value={stats.health}
          sub="Monto en Config. Nómina → TSS"
        />
        <StatCard
          Icon={AlertCircle}
          iconBg="bg-orange-100" iconCls="text-orange-600"
          label="Contactos Emergencia"
          value={stats.emergency}
          sub="Con datos de contacto"
        />
        <StatCard
          Icon={Heart}
          iconBg="bg-purple-100" iconCls="text-purple-600"
          label="Dep. Adicionales"
          value={stats.additional}
          sub="ISR / Beneficios fiscales"
        />
      </div>

      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-3">

          {/* Búsqueda */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar persona, empleado, documento, teléfono..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
            />
          </div>

          {/* Filtro empleado */}
          <div className="relative">
            <select
              value={filterEmployee}
              onChange={e => setFilterEmployee(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white appearance-none pr-8"
            >
              <option value="">Todos los empleados</option>
              {employees.filter(e => e.estado === 'activo').map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.nombre} {emp.apellido}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Filtro parentesco */}
          <div className="relative">
            <select
              value={filterRelationship}
              onChange={e => setFilterRelationship(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white appearance-none pr-8"
            >
              <option value="">Todos los parentescos</option>
              {RELATIONSHIPS.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Filtro rol */}
          <div className="relative">
            <select
              value={filterRole}
              onChange={e => setFilterRole(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white appearance-none pr-8"
            >
              <option value="">Todos los roles</option>
              <option value="health">Dep. Salud</option>
              <option value="additional">Dep. Adicional</option>
              <option value="emergency">Emergencia</option>
              <option value="beneficiary">Beneficiario</option>
            </select>
            <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Filtro estado */}
          <div className="relative">
            <select
              value={filterActive}
              onChange={e => setFilterActive(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white appearance-none pr-8"
            >
              <option value="true">Activos</option>
              <option value="false">Inactivos</option>
              <option value="">Todos</option>
            </select>
            <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Pill contador */}
          <span className="text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
            <span className="font-semibold text-gray-700">{filtered.length}</span> registro{filtered.length !== 1 ? 's' : ''}
          </span>

          {/* Refresh */}
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40"
            title="Actualizar"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>

          {/* Nueva persona */}
          <button
            onClick={handleNew}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={14} />
            Nueva Persona
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
            <p className="text-xs text-red-700">{error}</p>
          </div>
        )}
      </div>

      {/* ── Tabla ───────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Users className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-500 mb-1">
              No hay personas relacionadas registradas
            </p>
            <p className="text-xs text-gray-400">
              {searchText || filterEmployee || filterRelationship || filterRole
                ? 'Ajusta los filtros de búsqueda'
                : 'Crea el primer registro con el botón "Nueva Persona"'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-600 text-white">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Empleado</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Persona</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Parentesco</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Roles</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Edad</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Contacto</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Estado</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedPersons.map(p => {
                  const age = calcAge(p.birth_date);
                  const activeRoles = ROLES.filter(r => p[r.key]);
                  return (
                    <tr key={p.id} className={`hover:bg-gray-50 transition-colors ${!p.active ? 'opacity-60' : ''}`}>

                      {/* Empleado */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {p.empleado_foto ? (
                            <img src={p.empleado_foto} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-blue-600">
                                {p.empleado_nombre?.[0]?.toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-semibold text-gray-900 truncate max-w-[130px]">
                              {p.empleado_nombre} {p.empleado_apellido}
                            </p>
                            <p className="text-xs text-gray-400">{p.empleado_codigo}</p>
                          </div>
                        </div>
                      </td>

                      {/* Persona */}
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-gray-900">
                          {p.first_name} {p.last_name}
                        </p>
                        {p.document_number && (
                          <p className="text-xs text-gray-400">{p.document_number}</p>
                        )}
                      </td>

                      {/* Parentesco */}
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700 capitalize">
                          {REL_MAP[p.relationship] || p.relationship}
                        </span>
                        {p.gender && (
                          <p className="text-xs text-gray-400 capitalize">{p.gender}</p>
                        )}
                      </td>

                      {/* Roles */}
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {activeRoles.length > 0 ? activeRoles.map(r => (
                            <span
                              key={r.key}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${r.bg} ${r.text}`}
                            >
                              <r.Icon size={10} />
                              {r.label}
                            </span>
                          )) : (
                            <span className="text-xs text-gray-300 italic">Sin roles</span>
                          )}
                        </div>
                      </td>

                      {/* Edad */}
                      <td className="px-4 py-3 text-center">
                        {age !== null ? (
                          <span className="text-sm font-semibold text-gray-700">{age} años</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>

                      {/* Contacto */}
                      <td className="px-4 py-3">
                        {p.phone ? (
                          <span className="text-xs text-gray-600">{p.phone}</span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>

                      {/* Estado */}
                      <td className="px-4 py-3 text-center">
                        {p.active ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                            <CheckCircle size={11} /> Activo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                            <XCircle size={11} /> Inactivo
                          </span>
                        )}
                      </td>

                      {/* Acciones */}
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleEdit(p)}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Edit2 size={12} /> Editar
                          </button>
                          <button
                            onClick={() => handleDelete(p)}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 size={12} /> Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Pagination
          page={page}
          totalPages={totalPages}
          total={filtered.length}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      </div>

      {/* ── Modal ───────────────────────────────────────────────── */}
      {showModal && (
        <PersonModal
          person={editing}
          employees={employees.filter(e => e.estado === 'activo')}
          onSave={handleSaved}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
